/**
 * GraphQL Client Configuration
 * Connects to the .NET GraphQL API backend.
 *
 * All GraphQL requests are instrumented with OpenTelemetry:
 * - Each request creates a trace span with operation name and type
 * - Request/response metadata is logged at appropriate levels
 * - Errors are recorded on spans for distributed trace visibility
 */

import logger from '../logger';
import { withSpan } from '../telemetry';

// API configuration from environment
const GRAPHQL_ENDPOINT = import.meta.env.VITE_GRAPHQL_ENDPOINT || 'http://localhost:5000/graphql';
const API_TIMEOUT = parseInt(import.meta.env.VITE_API_TIMEOUT_MS || '15000', 10);

// Token storage key
const TOKEN_KEY = 'ridequest_auth_token';
const REFRESH_TOKEN_KEY = 'ridequest_refresh_token';

/**
 * Get stored auth token
 */
export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * Set auth token
 */
export function setAuthToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

/**
 * Get stored refresh token
 */
export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

/**
 * Set refresh token
 */
export function setRefreshToken(token: string): void {
  localStorage.setItem(REFRESH_TOKEN_KEY, token);
}

/**
 * Clear all auth tokens
 */
export function clearAuthTokens(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return !!getAuthToken();
}

/**
 * GraphQL error interface
 */
export interface GraphQLError {
  message: string;
  path?: string[];
  extensions?: Record<string, unknown>;
}

/**
 * GraphQL response interface
 */
export interface GraphQLResponse<T> {
  data?: T;
  errors?: GraphQLError[];
}

/**
 * Extract the GraphQL operation name from a query string.
 * Used for span naming and log context.
 */
function extractOperationName(query: string): string {
  const match = query.match(/(?:query|mutation)\s+(\w+)/);
  return match ? match[1] : 'unknown';
}

/**
 * Execute a GraphQL query or mutation.
 * Each request is wrapped in an OpenTelemetry span for distributed tracing.
 */
export async function graphqlRequest<T>(
  query: string,
  variables?: Record<string, unknown>,
  requiresAuth = false
): Promise<GraphQLResponse<T>> {
  const operationName = extractOperationName(query);
  const operationType = query.trimStart().startsWith('mutation') ? 'mutation' : 'query';

  return withSpan(`graphql.${operationName}`, async (span) => {
    // Enrich span with GraphQL-specific attributes
    span.setAttribute('graphql.operation.name', operationName);
    span.setAttribute('graphql.operation.type', operationType);
    span.setAttribute('graphql.requires_auth', requiresAuth);

    // DEBUG: Log every GraphQL request with operation context
    logger.debug(`GraphQL ${operationType} started: ${operationName}`, {
      operation: operationName,
      type: operationType,
      requiresAuth,
      hasVariables: !!variables,
    });

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    const token = getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      span.setAttribute('auth.token_present', true);
    } else if (requiresAuth) {
      // WARN: Auth required but no token available
      logger.warn(`GraphQL ${operationName} requires auth but no token present`);
      return {
        errors: [{ message: 'Authentication required' }],
      };
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

    try {
      const response = await fetch(GRAPHQL_ENDPOINT, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          query,
          variables,
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      span.setAttribute('http.status_code', response.status);

      if (!response.ok) {
        // ERROR: HTTP-level failure (network issues, server errors)
        logger.error(`GraphQL ${operationName} HTTP error`, undefined, {
          operation: operationName,
          status: response.status,
          statusText: response.statusText,
        });
        return {
          errors: [{ message: `HTTP error: ${response.status} ${response.statusText}` }],
        };
      }

      const result = await response.json();

      if (result.errors && result.errors.length > 0) {
        // WARN: GraphQL returned errors (validation, business logic)
        logger.warn(`GraphQL ${operationName} returned errors`, {
          operation: operationName,
          errorCount: result.errors.length,
          firstError: result.errors[0].message,
        });
        span.setAttribute('graphql.error_count', result.errors.length);
      } else {
        // DEBUG: Successful response
        logger.debug(`GraphQL ${operationName} completed successfully`);
      }

      return result as GraphQLResponse<T>;
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          // ERROR: Request timed out - could indicate API performance issues
          logger.error(`GraphQL ${operationName} timed out after ${API_TIMEOUT}ms`, error, {
            operation: operationName,
            timeout: API_TIMEOUT,
          });
          return {
            errors: [{ message: 'Request timeout' }],
          };
        }
        // ERROR: Network or other fetch error
        logger.error(`GraphQL ${operationName} failed`, error, {
          operation: operationName,
        });
        return {
          errors: [{ message: error.message }],
        };
      }

      // ERROR: Unknown error type
      logger.error(`GraphQL ${operationName} failed with unknown error`, undefined, {
        operation: operationName,
      });
      return {
        errors: [{ message: 'Unknown error occurred' }],
      };
    }
  });
}

/**
 * Helper to extract data from GraphQL response
 */
export function extractData<T>(response: GraphQLResponse<T>): { data: T | null; error: string | null } {
  if (response.errors && response.errors.length > 0) {
    return {
      data: null,
      error: response.errors[0].message,
    };
  }
  return {
    data: response.data || null,
    error: null,
  };
}

/**
 * GraphQL client instance for direct usage
 */
export const graphqlClient = {
  query: <T>(query: string, variables?: Record<string, unknown>) =>
    graphqlRequest<T>(query, variables, false),

  mutate: <T>(mutation: string, variables?: Record<string, unknown>) =>
    graphqlRequest<T>(mutation, variables, false),

  authenticatedQuery: <T>(query: string, variables?: Record<string, unknown>) =>
    graphqlRequest<T>(query, variables, true),

  authenticatedMutate: <T>(mutation: string, variables?: Record<string, unknown>) =>
    graphqlRequest<T>(mutation, variables, true),
};

export default graphqlClient;
